/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { Box, Center, Container, Flex, Tab, TabList, TabPanel, TabPanels, Tabs, Text, Tooltip } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { getFilterTitle, getYear } from '../../utils.js';
import { mappingKhmerNumber } from '../../../../utils/market-development.js';
import Company from '../../../../components/Company.js';
import BreadcrumbComponent from '../../../../components/BreadcrumbComponent/BreadcrumbComponent';
import PrintLayout from '../../../../components/Print/PrintLayout';
import SPLOCISpreadSheetTable from './SPLOCISpreadSheetTable';
import SFPSpreadSheetTable from './SFPSpreadSheetTable.js';
import Filter from '../ews-summary-filter.js';
import useStore from '../../../../store/index.js';
import c from '../../../../constant';
import ExportExcelSummarizedReportSPLOCI from '../../../../components/EarlyWarning/SummarizedReport/ExcelSummarizedReportSPLOCI.js';
import ExportExcelSummarizedReportSFP from '../../../../components/EarlyWarning/SummarizedReport/ExcelSummarizedReportSFP.js';
import { gql, useLazyQuery } from '@apollo/client';

const Q_REPORT_SFP = gql`
	query getEarlyWarningSFPSummaryReport($year: Float!, $companyTypes: [InsuranceInstitutionsType!] = [], $companyId: String) {
		getEarlyWarningSFPSummaryReport(year: $year, companyTypes: $companyTypes, companyId: $companyId)
	}
`;

const Q_REPORT_SPLOCI = gql`
	query getEarlyWarningSPLOCISummaryReport($year: Float!, $companyTypes: [InsuranceInstitutionsType!] = [], $companyId: String) {
		getEarlyWarningSPLOCISummaryReport(year: $year, companyTypes: $companyTypes, companyId: $companyId)
	}
`;

function SummarizedFinancialStatementReport() {
	const location = useLocation();
	const history = useHistory();
	const urlParams = new URLSearchParams(location.search);
	const period = urlParams.get('period') || null;
	const reportId = urlParams.get('reportId') || null;
	const report = urlParams.get('report') || null;
	const year = urlParams.get('year') || null;
	const [month, setMonth] = useState(null);
	const companyType = urlParams.get('companyType') || null;
	const companyId = urlParams.get('companyId') || null;
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const [breadcrumb, setBreadcrumb] = useState([]);
	const [tabIndex, setTabIndex] = useState(0);
	const [exportSFP, setExportSFP] = useState(null);
	const [exportSPLOCI, setExportSPLOCI] = useState(null);
	const [loadReportSFP, { loading, error, data: dataReportSFP }] = useLazyQuery(Q_REPORT_SFP);
	const [loadReportSPLOCI, { data: dataReportSPLOCI }] = useLazyQuery(Q_REPORT_SPLOCI);

	useEffect(() => {
		if (year) {
			loadReportSFP({
				variables: {
					year: parseFloat(year),
					companyTypes: companyType ? [companyType] : [],
					companyId: companyId || null,
				},
			});
			loadReportSPLOCI({
				variables: {
					year: parseFloat(year),
					companyTypes: companyType ? [companyType] : [],
					companyId: companyId || null,
				},
			});
		}
	}, [year, companyType, companyId]);

	useEffect(() => {
		if (period && reportId) {
			setBreadcrumb([
				{
					name: 'Company Report',
					path: '/company-submission-report',
				},
				{
					name: period,
					path: `/early-warning-system?period=${period}&reportId=${reportId}&page=1&report=${report}`,
				},
				{
					name: 'Summarized Financial Statements Report',
					path: '#',
				},
			]);
		} else {
			setBreadcrumb([
				{
					name: 'Company Report',
					path: '/company-submission-report',
				},
				{
					name: 'Summarized Financial Statements Report',
					path: '#',
				},
			]);
		}
	}, [period, reportId]);

	return (
		<PrintLayout>
			<>
				<Container maxW="container.xl" mt="16px" mb="16px">
					<BreadcrumbComponent list={breadcrumb} />
					<Center mt="5">
						<Box
							w="100%"
							bg="white"
							borderRadius={c.borderRadius}
							border="1px solid #dbdbdb"
							boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
							pb="32px"
						>
							<Flex
								className="hidden-print"
								bg="#FAFAFA"
								p="16px"
								pt="8px"
								pb="8px"
								mb="0px"
								alignItems="center"
								justifyContent="space-between"
								borderBottom="1px solid #dbdbdb"
							>
								<Text fontSize="x-large" fontWeight="bold">
									{t('Summarized Financial Statements Report')}
								</Text>
								<Flex>
									{tabIndex === 0 ? (
										<ExportExcelSummarizedReportSFP month={month} year={year} object={exportSFP} mr={2} />
									) : tabIndex === 1 ? (
										<ExportExcelSummarizedReportSPLOCI month={month} year={year} object={exportSPLOCI} mr={2} />
									) : null}
									<Filter
										period={period}
										reportId={reportId}
										report={report}
										year={year}
										companyId={companyId}
										companyType={companyType}
										onCallback={(url) => history.push(`/early-warning-system/summarized-fs-report${url}`)}
									/>
								</Flex>
							</Flex>
							<Flex flexDirection="column" justifyContent="center" alignItems="center" pt={4} pl={4} pr={4} pb={1}>
								<Text fontSize="xl" fontWeight="700" color="blue.500" mb={1}>
									{t(getFilterTitle(companyType))}
								</Text>
								{companyId && (
									<Box mb={1}>
										<Company id={companyId} color="blue.500" />
									</Box>
								)}
								<Text fontSize="xl" fontWeight="700" color="blue.500">
									{lang === 'kh' ? mappingKhmerNumber(getYear(year)) : getYear(year)}
								</Text>
							</Flex>
							<Box pt={0} pl={4} pr={4}>
								<Tabs index={tabIndex} onChange={(index) => setTabIndex(index)}>
									<TabList borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor="gray.200">
										<Tab>
											<Tooltip label={t('SFP')}>
												<Text>{'SFP'}</Text>
											</Tooltip>
										</Tab>
										<Tab>
											<Tooltip label={t('SPLOCI')}>
												<Text>{'SPLOCI'}</Text>
											</Tooltip>
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel pl={0} pr={0} pb={0}>
											<SFPSpreadSheetTable
												year={year}
												companyType={companyType}
												companyId={companyId}
												onReadyForExport={(result) => setExportSFP(result)}
											/>
										</TabPanel>
										<TabPanel pl={0} pr={0} pb={0}>
											<SPLOCISpreadSheetTable
												year={year}
												companyType={companyType}
												companyId={companyId}
												onReadyForExport={(result) => setExportSPLOCI(result)}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</Box>
						</Box>
					</Center>
				</Container>
			</>
		</PrintLayout>
	);
}

export default SummarizedFinancialStatementReport;
